// Confirm dialog store — promise-based replacement for window.confirm.
//
// Usage:
//   import { confirm } from "@/stores/confirmStore";
//   const ok = await confirm({
//     title: "Delete engagement?",
//     message: "All findings, evidence, and scan history will be removed. This cannot be undone.",
//     variant: "danger",
//     confirmLabel: "Delete",
//   });
//   if (!ok) return;
//
// Renders via <ConfirmHost /> mounted once in AppLayout. Only one
// dialog can be open at a time — calling confirm() while another is
// open silently overwrites the first.

import { create } from "zustand";

export type ConfirmVariant = "default" | "danger";

export interface ConfirmRequest {
  title: string;
  message: string;
  variant?: ConfirmVariant;
  /** Defaults to "Confirm" / "Delete" depending on variant. */
  confirmLabel?: string;
  /** Defaults to "Cancel". */
  cancelLabel?: string;
}

interface OpenState extends ConfirmRequest {
  resolve: (ok: boolean) => void;
}

interface ConfirmState {
  open: OpenState | null;
  request: (req: ConfirmRequest) => Promise<boolean>;
  resolve: (ok: boolean) => void;
}

export const useConfirmStore = create<ConfirmState>((set, get) => ({
  open: null,
  request: (req) =>
    new Promise<boolean>((resolve) => {
      set({ open: { ...req, resolve } });
    }),
  resolve: (ok) => {
    const current = get().open;
    if (current) {
      current.resolve(ok);
      set({ open: null });
    }
  },
}));

/** Convenience facade — same shape as window.confirm but with a richer
 *  API and on-brand UI. Returns Promise<boolean>. */
export function confirm(req: ConfirmRequest): Promise<boolean> {
  return useConfirmStore.getState().request(req);
}
